import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);

  // Get user ID (use localStorage fallback for guests)
  const getUserId = useCallback(() => {
    if (user?.id || user?._id) {
      return user.id || user._id;
    }
    // Guest user - use localStorage ID
    let guestId = localStorage.getItem('guest_id');
    if (!guestId) {
      guestId = 'guest_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('guest_id', guestId);
    }
    return guestId;
  }, [user]);

  // Fetch cart from API
  const fetchCart = useCallback(async () => {
    const userId = getUserId();
    setLoading(true);
    try {
      const response = await cartAPI.get(userId);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [getUserId]);

  // Load cart on mount and when user changes
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Add item to cart
  const addToCart = async (productId, quantity = 1, productName = 'Product', productImage = '') => {
    const userId = getUserId();
    try {
      await cartAPI.addItem(userId, productId, quantity);
      await fetchCart();
      
      // Dispatch custom event for cart animation
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { action: 'add' } }));
      
      toast.success('Added to Cart!', {
        description: productName.length > 50 ? productName.substring(0, 50) + '...' : productName,
        action: {
          label: 'View Cart',
          onClick: () => window.location.href = '/cart'
        },
        duration: 4000
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to Add', {
        description: 'Could not add item to cart. Please try again.'
      });
    }
  };

  // Update item quantity
  const updateQuantity = async (productId, quantity) => {
    const userId = getUserId();
    try {
      await cartAPI.updateQuantity(userId, productId, quantity);
      await fetchCart();
      toast.success('Cart Updated');
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error('Error', {
        description: 'Failed to update quantity.'
      });
    }
  };

  // Remove item from cart
  const removeFromCart = async (productId) => {
    const userId = getUserId();
    try {
      await cartAPI.removeItem(userId, productId);
      await fetchCart();
      toast.success('Removed', {
        description: 'Item removed from cart.'
      });
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Error', {
        description: 'Failed to remove item.'
      });
    }
  };

  // Clear cart
  const clearCart = async () => {
    const userId = getUserId();
    try {
      await cartAPI.clear(userId);
      await fetchCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      fetchCart,
      itemCount: cart.item_count || 0,
      subtotal: cart.subtotal || 0
    }}>
      {children}
    </CartContext.Provider>
  );
};
